import { Link } from "react-router-dom"
import styled from "styled-components"
import { rgba } from "polished"
import colors from "~constants/colors"

export const AppContainer = styled.div`
  background-color: ${colors.gray5};
  display: flex;
  height: 100vh;
`

// Reserves horizontal space in the flex layout: 64px when the sidebar is an
// (auto-expanding) rail, 220px when pinned. The rail width does NOT react to
// hover, so the content never reflows while the panel expands over it.
export const SidebarRail = styled.div`
  position: relative;
  flex-shrink: 0;
  width: 64px;
  min-width: 64px;
  transition:
    width 0.2s ease,
    min-width 0.2s ease;

  &.pinned {
    width: 220px;
    min-width: 220px;
  }
`

// The actual sidebar panel. Absolutely positioned inside the rail so that, when
// it expands (hover or pinned), it overlays the page content instead of pushing
// it. Collapsed = icon-only rail; expanded = 220px with labels.
export const NavBarContainer = styled.nav`
  position: absolute;
  top: 0;
  left: 0;
  bottom: 0;
  /* Above sticky page content (e.g. the POO list at z-index 1000) so the panel,
     when it expands over the content on hover, isn't covered by it. */
  z-index: 1100;
  background: white;
  width: 220px;
  display: flex;
  flex-direction: column;
  border-right: 1px solid ${colors.gray2};
  box-shadow: 2px 0 8px rgba(0, 0, 0, 0.04);
  transition: width 0.2s ease;
  overflow: hidden;

  > .navbar-logo {
    margin: 24px 20px;
    width: calc(100% - 40px) !important;
    transition: all 0.2s ease;
    object-fit: contain;
  }

  > ul {
    list-style-type: none;
    margin: 0;
    padding: 0;
    flex: 1;
  }

  > .site-version {
    align-content: flex-end;
    padding: 10px;
    text-align: center;
    transition: opacity 0.15s ease;
  }

  &.collapsed {
    width: 64px;

    > .navbar-logo {
      margin: 16px auto;
      width: 32px !important;
    }

    > .site-version {
      opacity: 0;
      height: 0;
      padding: 0;
      overflow: hidden;
    }
  }
`

export const NavItem = styled.li`
  margin: 0 0 4px 0;
  padding: 0 12px;
  position: relative;

  > a {
    border-radius: 6px;
    color: ${colors.cerulean};
    display: flex;
    align-items: center;
    font-weight: 600;
    line-height: 44px;
    padding: 0 10px;
    text-decoration: none;
    white-space: nowrap;
    overflow: hidden;
    transition:
      background-color 0.15s ease,
      color 0.15s ease;

    &.active {
      background-color: ${rgba(colors.blue2, 0.1)};
      color: ${colors.blue1};

      &:after {
        content: "";
        display: inline-block;
        position: absolute;
        right: 0;
        top: 0;
        bottom: 0;
        width: 2px;
        background-color: ${colors.caribbean};
      }
    }

    &:hover:not(.active) {
      background-color: ${colors.gray1};
    }

    > svg {
      font-size: 22px;
      margin-right: 10px;
      flex-shrink: 0;
      transition: margin 0.2s ease;
    }

    > .nav-label {
      transition: opacity 0.15s ease;
    }
  }

  .collapsed & {
    padding: 0 8px;

    > a {
      justify-content: center;
      padding: 0;
      height: 44px;

      > svg {
        margin-right: 0;
      }

      > .nav-label {
        opacity: 0;
        width: 0;
        overflow: hidden;
        position: absolute;
        pointer-events: none;
      }
    }

    &:hover::after {
      content: attr(data-tooltip);
      position: absolute;
      left: calc(100% + 10px);
      top: 50%;
      transform: translateY(-50%);
      background: ${colors.gray3};
      color: ${colors.white};
      padding: 6px 12px;
      border-radius: 6px;
      font-size: 13px;
      font-weight: 500;
      white-space: nowrap;
      z-index: 10000;
      pointer-events: none;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    }
  }
`

export const SidebarToggle = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 12px 12px;
  padding: 8px;
  border: 1px solid ${colors.gray2};
  border-radius: 8px;
  background: ${colors.gray1};
  color: ${colors.gray6};
  cursor: pointer;
  transition: all 0.15s ease;
  flex-shrink: 0;

  &:hover {
    background: ${colors.gray1};
    color: ${colors.gray3};
    border-color: ${colors.gray4};
  }

  &.active {
    background: ${rgba(colors.blue2, 0.12)};
    color: ${colors.blue1};
    border-color: ${colors.blue2};
  }

  > svg {
    font-size: 20px;
  }
`

export const ContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100vh;
  overflow: auto;
  flex: 1;
  min-width: 0;

  > main,
  > main > * {
    height: auto;
  }
`

export const MainContainer = styled.div`
  padding: 18px 24px 24px;
`

export const HeaderContainer = styled.div`
  align-items: center;
  display: flex;
  justify-content: space-between;
  background-color: ${colors.white};
  padding: 12px 24px;
  border-bottom: 1px solid ${colors.gray2};
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);

  .companyDetailsContainer {
    display: flex;
    align-items: center;
    > .logoContainer {
      width: 45px;
      height: 45px;
      border: 1px solid ${colors.gray2};
      display: flex;
      border-radius: 12px;
      justify-content: center;
      vertical-align: middle;
      margin-right: 16px;
      > img {
        align-self: center;
        height: 30px;
        width: 30px;
        margin: 0;
      }
    }
  }

  .rightContent {
    margin-left: auto;
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 12px;
  }

  .userDetailsContainer {
    margin-left: 12px;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    justify-content: center;
    gap: 2px;
  }

  .userName {
    font-weight: 600;
    font-size: 14px;
    line-height: 1.25;
    color: ${colors.gray3};
    letter-spacing: 0.01em;
  }

  .userRole {
    font-size: 12px;
    line-height: 1.25;
    color: ${colors.gray6};
    font-weight: 500;
  }
`

export const HeaderUserBlock = styled.div`
  display: flex;
  align-items: center;
  padding: 6px 14px 6px 6px;
  background: ${colors.gray5};
  border: 1px solid ${colors.gray2};
  border-radius: 10px;
`

export const HeaderLogoutLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 14px;
  font-size: 13px;
  font-weight: 600;
  color: ${colors.gray6};
  text-decoration: none;
  border-radius: 8px;
  border: 1px solid transparent;
  transition:
    color 0.15s ease,
    background 0.15s ease,
    border-color 0.15s ease;

  &:hover {
    color: ${colors.gray3};
    background: ${colors.gray1};
    border-color: ${colors.gray2};
  }

  &:focus-visible {
    outline: 2px solid ${colors.blue1};
    outline-offset: 2px;
  }

  svg {
    width: 18px;
    height: 18px;
    opacity: 0.9;
  }
`

export const PageTitle = styled.h2`
  color: ${colors.gray3};
  display: inline-block;
  font-size: 20px;
  line-height: 36px;
  margin: 0;
`
